import React, { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Button, Loading } from '@wfp/ui';

import { usePreviewMonth, usePreviewTypeIsDoc } from '../../hooks/preview';
import { addMonths, previousDay } from '../../utils/SimpleDate';
import { getCalendar } from '../../restapi/warning';
import { FullWarningEvent, WarningEventStatus, WarningEventType } from '../../restapi/types';
import { extractEvents } from './utils';
import { formatDateShort } from '../../utils/utils';
import { approveEvents } from '../../restapi/warningEvent';
import { DocPreview } from './DocPreview';
import { TablePreview } from './TablePreview';
import confirm from '../common/dialogs/Confirm';


export function DataLoaderPreview() {
	const from = usePreviewMonth();
	const to = previousDay(addMonths(from, 1));
	const isDoc = usePreviewTypeIsDoc();
	const queryClient = useQueryClient();

	const calendar = useQuery(['calendar', from, to], () => getCalendar(from, to));
	const [selectedEventIds, setSelectedEventIds] = useState<number[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const approveMutation = useMutation((ids: number[]) => approveEvents(ids), {
		onMutate: () => setIsSaving(true),
		onSuccess: () => {
			setSelectedEventIds([]);
			queryClient.invalidateQueries('calendar');
			confirm({message: "Changes have been approved."});
		},
		onSettled: () => {
			setIsSaving(false);
		},
	});

	const approveAll = useCallback(
		(unapprovedEvents: FullWarningEvent[], selection: number[]) => {
			approveMutation.mutate(
				selection.length ? selection : unapprovedEvents.map(e => e.id),
			);
		}, [approveMutation],
	);

	if (!calendar.data) {
		return <Loading withOverlay={false} active small/>;
	}

	const events = extractEvents(calendar.data,
		e => isDoc ? (e.type === WarningEventType.raised) : (e.type !== WarningEventType.closed));

	const closedEvents = isDoc ? [] : extractEvents(calendar.data, e => e.type === WarningEventType.closed);

	const approvableEvents = isDoc ? events : [
		...events.filter(e => e.type !== WarningEventType.raised),
		...closedEvents,
	];

	const unapprovedEvents = approvableEvents.filter(e => e.status !== WarningEventStatus.approved);
	const canApprove = unapprovedEvents.length >= 1;
	const allSelected = (selectedEventIds.length === 0) || (selectedEventIds.length === unapprovedEvents.length);

	return (events.length + closedEvents.length) ? <div>
		{isDoc ?
			<DocPreview
				events={events}
				selected={selectedEventIds}
				onSelectionChange={s => setSelectedEventIds(s)}
			/> :
			<>
				{events.length ? <TablePreview
					events={events}
					selected={selectedEventIds}
					onSelectionChange={s => setSelectedEventIds(s)}
				/> : null}
				{closedEvents.length ? <>
					<h5 className="mt2">Closed warning</h5>
					<TablePreview
						events={closedEvents}
						selected={selectedEventIds}
						onSelectionChange={s => setSelectedEventIds(s)}
					/>
				</> : null}
			</>}

		{canApprove && <div className="mt2 right">
			<div className="fleft">
				{(allSelected && selectedEventIds.length > 0) ?
					<Button kind="primary" disabled={isSaving} onClick={() => setSelectedEventIds([])}>
						Select none
					</Button>
					:
					<Button kind="primary" disabled={isSaving} onClick={() => setSelectedEventIds(unapprovedEvents.map(e => e.id))}>
						Select all
					</Button>
				}
			</div>
			<Button kind="primary" disabled={isSaving} onClick={() => approveAll(unapprovedEvents, selectedEventIds)}>
				Approve {allSelected ? 'all' : 'selected'}
			</Button>
			{isDoc ? <p className="note mt">
				By approving what you see here you also approve the updates provided for raised warnings.
			</p> : <p className="note mt">
				To approve updates provided for raised warnings refer to Monthly Report preview page.
			</p>}
		</div>}
	</div> : <p>No warning has been {isDoc ? 'raised' : 'updated'} in {formatDateShort(from)}.</p>;
}
